<template>
  <div id="app">
    <LoadingScreen v-if="loading" />
    <template v-else>
      <div class="background"></div>
      <Notification />
      <component 
        :is="currentView" 
        @view-changed="changeView" 
        :app-balance="displayBalance"
        @add-click="addClick"
        @balance-updated="onBalanceUpdated"
        @total-coins-earned-updated="onTotalCoinsEarnedUpdated"
      />
      <TapBar :current-view="currentView" @change-view="changeView" />
    </template>
  </div>
</template>

<script>
import MainScreen from './views/MainScreen.vue';
import ReferralsView from './views/ReferralsView.vue';
import TasksView from './views/TasksView.vue';
import TapBar from './components/TapBar.vue';
import Notification from './components/Notification.vue';
import LoadingScreen from './components/LoadingScreen.vue';

// Import Supabase functions
import { createUser, getBalance, updateBalance } from './supabase'

// Import global store
import { loadStoreFromLocalStorage } from './store.js';

export default {
  name: 'App',
  components: { MainScreen, ReferralsView, TasksView, TapBar, Notification, LoadingScreen },
  data() {
    return {
      telegram_id: null,
      balance: 0,
      loading: true,
      currentView: 'MainScreen'
    }
  },
  async mounted() {
    // Initialize global store first
    loadStoreFromLocalStorage();
    
    const tg = window.Telegram?.WebApp
    this.telegram_id = tg?.initDataUnsafe?.user?.id

    // Check if Telegram WebApp is available
    if (!tg) {
      console.warn('Telegram WebApp not available, using local storage for development');
    }

    if (!this.telegram_id) {
      console.log('Telegram ID not found, using local storage')
      // Load balance from localStorage for local development
      const savedBalance = localStorage.getItem('astro_local_balance');
      this.balance = savedBalance ? parseInt(savedBalance) : 327;
      console.log('Loaded local balance:', this.balance);
      
      // Добавляем задержку в 4 секунды для заставки
      await new Promise(resolve => setTimeout(resolve, 4000));
      this.loading = false
      return
    }

    // Инициализация пользователя и синхронизация баланса сразу
    await this.initUser()
    
    // Добавляем задержку в 4 секунды для заставки
    await new Promise(resolve => setTimeout(resolve, 4000));
    this.loading = false
    
    // Listen for debug balance updates
    window.addEventListener('astro:balance-update', (event) => {
      console.log('=== WINDOW BALANCE UPDATE EVENT ===');
      console.log('Received balance:', event.detail.balance);
      this.balance = event.detail.balance;
      console.log('App balance updated to:', this.balance);
    });
  },
  methods: {
    changeView(view) {
      this.currentView = view;
      localStorage.setItem('astroclicker-current-view', view);
    },
    
    // Загружаем пользователя и баланс
    async initUser() {
      try {
        let balance = await getBalance(this.telegram_id)

        if (balance === null) {
          // Если юзер не найден → создаём
          await createUser(this.telegram_id)
          this.balance = 0
        } else {
          this.balance = balance
        }
      } catch (error) {
        console.error('Error initializing user:', error)
        this.balance = 0
      }
    },

    // Клик → увеличение баланса с реальным временем сохранения
    async addClick() {
      // Получаем множитель из gameApi
      const gameApi = window.AstroGameApi;
      const baseGain = gameApi?.state?.clickPower || 1;
      const isBoostActive = gameApi?.state?.boostActive || false;
      const gain = isBoostActive ? baseGain * 2 : baseGain;
      
      this.balance += gain;
      
      // Сохраняем баланс в реальном времени
      if (this.telegram_id) {
        // Online mode - save to Supabase
        try {
          await updateBalance(this.telegram_id, this.balance)
        } catch (error) {
          console.error('Error updating balance:', error)
        }
      } else {
        // Offline mode - save to localStorage
        localStorage.setItem('astro_local_balance', this.balance.toString());
      }
    },
    
    onBalanceUpdated(newBalance) {
      console.log('=== BALANCE UPDATED IN APP ===');
      console.log('Old balance:', this.balance);
      console.log('New balance:', newBalance);
      this.balance = newBalance;
      console.log('Balance set to:', this.balance);
      
      // Always save to localStorage to maintain consistency
      localStorage.setItem('astro_local_balance', this.balance.toString());
      
      // Update Supabase if in online mode
      if (this.telegram_id) {
        updateBalance(this.telegram_id, this.balance).catch(error => {
          console.error('Error updating balance in Supabase:', error);
        });
      }
    },
    
    onTotalCoinsEarnedUpdated(totalCoins) {
      console.log('=== TOTAL COINS EARNED UPDATED IN APP ===');
      console.log('New total coins earned:', totalCoins);
      
      // Save to localStorage to maintain consistency
      localStorage.setItem('astro_total_coins_earned', totalCoins.toString());
    }
  },
  computed: {
    displayBalance() {
      return this.balance
    }
  }
};
</script>

<style>
/* Global styles to prevent scrolling */
html, body {
  overflow: hidden;
  height: 100%;
  touch-action: none;
  -webkit-overflow-scrolling: auto;
}

#app {
  height: 100vh;
  overflow: hidden;
  position: relative;
  background: #000000;
}

/* Prevent pull-to-refresh on mobile */
body {
  overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;
}

/* Disable text selection and dragging */
* {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}

/* Allow text selection only for input elements */
input, textarea {
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}

/* Font styles */
* {
  font-family: 'Inter Rounded', 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  font-weight: 500;
}

/* Headers - SF Pro Rounded */
h1, h2, h3, h4, h5, h6, 
.header, .title, .section-title, 
.boost-button, .upgrade-button {
  font-family: 'SF Pro Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  font-weight: 600;
}

/* Numbers/Balance - Poppins Medium */
.balance-display, .coin-count, 
.energy-indicator, .button-cost, 
.level-number, .reward-amount {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  font-weight: 500;
}
</style>